
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Sample, AnalysisCatalogItem } from '../../types';
import { Printer, FileJson, FileCode } from 'lucide-react';
import { generateFHIRBundle, generateHL7Message } from '../../lib/interop';

interface SampleReportProps {
  selectedSample: Sample | null;
  onClose: () => void;
  onPrint: () => void;
}

const SampleReport: React.FC<SampleReportProps> = ({ selectedSample, onClose, onPrint }) => {
  const [catalog, setCatalog] = useState<AnalysisCatalogItem[]>([]);

  useEffect(() => {
    const fetchCatalog = async () => {
        if (!isSupabaseConfigured()) {
            setCatalog([
                { id: '1', name: 'Microscopie', possible_results: [], created_at: '', loinc_code: '11545-1' },
                { id: '2', name: 'GeneXpert', possible_results: [], created_at: '', loinc_code: '91465-5' },
                { id: '3', name: 'Culture', possible_results: [], created_at: '', loinc_code: '584-3', unit: 'CFU/mL' }
            ]);
            return;
        }
        const { data } = await supabase.from('analysis_catalog').select('*');
        if (data) setCatalog(data as AnalysisCatalogItem[]);
    };
    fetchCatalog();
  }, []);

  if (!selectedSample) return null;

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportFHIR = () => {
    try {
        const bundle = generateFHIRBundle(selectedSample, catalog);
        downloadFile(JSON.stringify(bundle, null, 2), `fhir-bundle-${selectedSample.id}.json`, 'application/json');
    } catch (e: any) {
        alert("Erreur lors de la génération FHIR: " + e.message);
    }
  };

  const handleExportHL7 = () => {
    try {
        const msg = generateHL7Message(selectedSample, catalog);
        downloadFile(msg, `hl7-oru-${selectedSample.id}.hl7`, 'text/plain');
    } catch (e: any) {
        alert("Erreur lors de la génération HL7: " + e.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white print:inset-0">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col h-[90vh] print:h-auto print:shadow-none print:w-full print:max-w-none print:rounded-none">
        
        <style type="text/css" media="print">
          {`
            @page { size: A4 portrait; margin: 20mm; }
            body { -webkit-print-color-adjust: exact; }
          `}
        </style>

        {/* Modal Header (Hidden on Print) */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center print:hidden shrink-0">
          <h3 className="font-bold text-lg text-slate-800">Aperçu du Rapport</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-auto p-12 print:p-0 print:overflow-visible">
          <div className="max-w-3xl mx-auto print:max-w-none">
             
             {/* Report Header */}
             <div className="flex justify-between items-start border-b-2 border-slate-800 pb-6 mb-8">
                 <div className="flex items-center gap-5">
                     <img src="/logoipm.png" alt="Institut Pasteur de Madagascar" className="h-24 w-auto object-contain" />
                     <div>
                         <h1 className="text-2xl font-bold text-slate-900">SIL Mycobactérie</h1>
                         <p className="text-sm font-semibold text-slate-600">Institut Pasteur de Madagascar</p>
                         <p className="text-xs text-slate-500">BP 1274 - Antananarivo 101</p>
                     </div>
                 </div>
                 <div className="text-right pt-2">
                     <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wide mb-1">Rapport d'Analyse</h2>
                     <p className="text-sm font-mono text-slate-500">#{selectedSample.id.slice(0,8).toUpperCase()}</p>
                     <div className={`inline-block mt-2 px-3 py-1 rounded text-xs font-bold uppercase ${selectedSample.status === 'validated' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-orange-100 text-orange-800 border border-orange-200'}`}>
                         {selectedSample.status === 'validated' ? 'Rapport Final' : 'Rapport Provisoire'}
                     </div>
                 </div>
             </div>

             {/* Patient & Sample Info */}
             <div className="grid grid-cols-2 gap-8 mb-8">
                 <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 print:bg-transparent print:border-slate-200 print:p-0">
                     <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 tracking-wider">Patient</h3>
                     <div className="space-y-1">
                         <p className="font-bold text-lg text-slate-900">{selectedSample.patients?.last_name} {selectedSample.patients?.first_name}</p>
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">ID Patient:</span> <span className="font-mono">{selectedSample.patients?.unique_id}</span></p>
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">Sexe:</span> {selectedSample.patients?.gender === 'M' ? 'Masculin' : 'Féminin'}</p>
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">Né(e) le:</span> {selectedSample.patients?.birth_date}</p>
                     </div>
                 </div>
                 <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 print:bg-transparent print:border-slate-200 print:p-0">
                     <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 tracking-wider">Prélèvement</h3>
                     <div className="space-y-1">
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">Nature:</span> <span className="font-semibold">{selectedSample.sample_type}</span></p>
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">Date prél.:</span> {new Date(selectedSample.collection_date).toLocaleDateString()}</p>
                         <p className="text-sm"><span className="text-slate-500 w-24 inline-block">Reçu le:</span> {new Date(selectedSample.created_at).toLocaleDateString()}</p>
                         {selectedSample.prescriber && (
                            <p className="text-sm border-t border-slate-200 mt-2 pt-1"><span className="text-slate-500 w-24 inline-block">Prescripteur:</span> <span className="font-medium">{selectedSample.prescriber}</span></p>
                         )}
                     </div>
                 </div>
             </div>

             {/* Results Table */}
             <div className="mb-12">
                 <h3 className="text-sm font-bold uppercase text-slate-800 mb-4 border-b border-slate-200 pb-2">Résultats</h3>
                 <table className="w-full text-left text-sm">
                     <thead>
                         <tr className="border-b border-slate-200">
                             <th className="py-2 text-slate-500 font-medium w-1/3">Examen (LOINC)</th>
                             <th className="py-2 text-slate-500 font-medium w-1/3">Résultat</th>
                             <th className="py-2 text-slate-500 font-medium text-right w-1/3">Date Validation</th>
                         </tr>
                     </thead>
                     <tbody className="divide-y divide-slate-100">
                         {selectedSample.results && selectedSample.results.length > 0 ? (
                             selectedSample.results.map((r, i) => {
                                 const item = catalog.find(c => c.name === r.analysis);
                                 return (
                                     <tr key={i}>
                                         <td className="py-3">
                                            <div className="font-semibold text-slate-800">{r.analysis}</div>
                                            {item?.loinc_code && <div className="text-xs font-mono text-slate-400">LN: {item.loinc_code}</div>}
                                         </td>
                                         <td className="py-3 font-mono text-slate-900">
                                            {r.result || <span className="text-slate-400 italic">En cours...</span>}
                                            {r.result && item?.unit && <span className="text-xs text-slate-500 ml-1">{item.unit}</span>}
                                         </td>
                                         <td className="py-3 text-right text-slate-600">{r.result ? new Date(r.date).toLocaleDateString() : '-'}</td>
                                     </tr>
                                 );
                             })
                         ) : (
                             <tr><td colSpan={3} className="py-4 text-center text-slate-400 italic">Aucun résultat disponible</td></tr>
                         )}
                     </tbody>
                 </table>
                 {selectedSample.status !== 'validated' && (
                     <div className="mt-4 p-3 bg-orange-50 text-orange-800 text-xs border border-orange-100 rounded print:border-none print:text-slate-500 print:italic">
                         * Ce rapport contient des résultats partiels et n'est pas encore validé définitivement.
                     </div>
                 )}
             </div>

             {/* Footer */}
             <div className="flex justify-between items-end mt-20 pt-8 border-t border-slate-200 print:mt-12">
                 <div className="text-xs text-slate-400">
                     <p>Imprimé le {new Date().toLocaleDateString()} à {new Date().toLocaleTimeString()}</p>
                     <p>SIL Mycobactérie v1.1 - HL7/FHIR Compliant</p>
                 </div>
                 <div className="text-center">
                     <div className="h-16 mb-2"></div> {/* Space for signature */}
                     <p className="text-sm font-bold text-slate-800 border-t border-slate-300 pt-2 px-8 inline-block">Validation Biologiste</p>
                 </div>
             </div>

          </div>
        </div>

        {/* Modal Footer Actions (Hidden on Print) */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center print:hidden shrink-0">
           
           <div className="flex gap-2">
               <button onClick={handleExportFHIR} className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg flex items-center gap-2 hover:bg-slate-50 shadow-sm text-sm">
                  <FileCode size={16} className="text-orange-600" /> Export FHIR (JSON)
               </button>
               <button onClick={handleExportHL7} className="px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg flex items-center gap-2 hover:bg-slate-50 shadow-sm text-sm">
                  <FileJson size={16} className="text-blue-600" /> Export HL7 (v2)
               </button>
           </div>

           <div className="flex gap-3">
               <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Fermer</button>
               <button onClick={onPrint} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700 shadow-sm">
                  <Printer size={16} /> Imprimer
               </button>
           </div>
        </div>
      </div>
    </div>
  );
};

export default SampleReport;
